import {
  type ApiEndpointBuilder,
  optionalMutationBody,
  optionalQueryParams,
} from "../apiEndpointHelpers";

import type {
  AdminV2GrowthBroadcastReviewedSendProof,
  AdminV2GrowthCampaignMetricsResponse,
  AdminV2GrowthCampaignSendResponse,
  AdminV2GrowthCampaignsListResponse,
  AdminV2GrowthCampaignStatus,
  JsonValue as AdminV2JsonValue,
  AdminV2MessagingCategory,
  AdminV2MessagingChannel,
} from "@/types/adminV2";
import type {
  BroadcastStopRequest,
  BroadcastStopResponse,
} from "@/types/adminV2/broadcastStop";
import type {
  AdminV2BroadcastCampaignRecordResponse,
  AdminV2CampaignContentDraft,
  AdminV2CampaignContentPreviewRequest,
  AdminV2CampaignContentPreviewResponse,
  AdminV2CampaignContentTestPreviewRequest,
  AdminV2CampaignContentTestPreviewResponse,
  AdminV2CampaignContentTestSendRequest,
  AdminV2CampaignRecipientExclusion,
} from "@/types/adminV2/growth";
import type { AdminV2MessagingTemplateSelfTestSendResponse } from "@/types/adminV2/messagingTemplateSelfTest";
import type { AdminV2RecipientLocalScheduleRequest } from "@/types/adminV2/recipientLocalSchedule";
import type {
  AdminV2CampaignAudienceReview,
  AdminV2CampaignConditionsPreviewResponse,
  AdminV2CampaignConditionsSource,
  AdminV2CampaignPreviewQuery,
  AdminV2CampaignReviewChecks,
  AdminV2CampaignReviewPreconditions,
  CampaignEligibleOnlyReview,
} from "@/types/adminV2CampaignConditions";
import type { AdminV2SharedSegmentSelection } from "@/types/adminV2SharedSegments";

export interface AdminV2GrowthCampaignDeliveryConfiguration {
  contentArtifactDigest?: string | null;
  contentDigest?: string | null;
  globalDesignVersionId?: string | null;
  globalDesignDigest?: string | null;
  senderConfigFingerprint: string;
  senderDisplayName: string;
  senderEmail: string;
  senderIdentityId: string;
  senderPostmarkMessageStream: string;
  senderReplyRouting: "support" | "marketing";
  templateVersionId: string;
  templateVersionNumber: number;
}

export type AdminV2GrowthCampaignPreviewResponseWithDelivery = Omit<
  AdminV2CampaignConditionsPreviewResponse,
  "preview"
> & {
  preview: AdminV2CampaignConditionsPreviewResponse["preview"] & {
    deliveryConfiguration: AdminV2GrowthCampaignDeliveryConfiguration | null;
  };
};

export const buildAdminV2GrowthCampaignsEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  stopAdminV2GrowthCampaign: builder.mutation<
    BroadcastStopResponse,
    { campaignId: string; body: BroadcastStopRequest }
  >({
    query: ({ campaignId, body }) => ({
      url: `/admin/v2/growth/campaigns/${campaignId}/stop`,
      method: "POST",
      data: body,
    }),
    invalidatesTags: (_result, _error, { campaignId }) => [
      { type: "AdminV2GrowthCampaigns", id: campaignId },
      { type: "AdminV2GrowthCampaigns", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),
  getAdminV2GrowthCampaigns: builder.query<
    AdminV2GrowthCampaignsListResponse,
    {
      organisationId?: string;
      q?: string;
      status?: AdminV2GrowthCampaignStatus;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/growth/campaigns",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2GrowthCampaigns", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  createAdminV2GrowthCampaign: builder.mutation<
    AdminV2BroadcastCampaignRecordResponse,
    {
      organisationId: string;
      remainderOfCampaignId?: string;
      name: string;
      description?: string | null;
      conditionsSource?: AdminV2CampaignConditionsSource;
      segmentId?: string;
      sharedSegmentAudience?: AdminV2SharedSegmentSelection;
      crmActionScopeId?: string;
      crmActionScopeDigest?: string;
      crmActionAudienceMode?: "primary_user" | "all_users";
      templateId: string;
      senderIdentityId?: string | null;
      channel: AdminV2MessagingChannel;
      category: AdminV2MessagingCategory;
      metadata?: AdminV2JsonValue;
      contentDraft?: AdminV2CampaignContentDraft;
      recipientExclusions?: AdminV2CampaignRecipientExclusion[];
      copyRecipientExclusions?: { sourceCampaignId: string; handles: string[] };
    }
  >({
    query: (body) => ({
      url: "/admin/v2/growth/campaigns",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2GrowthCampaigns", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  getAdminV2GrowthCampaign: builder.query<
    AdminV2BroadcastCampaignRecordResponse,
    {
      campaignId: string;
      /** Client cache partition only; server auth still owns access. */ readOwner?: string;
    }
  >({
    query: ({ campaignId }) => ({
      url: `/admin/v2/growth/campaigns/${campaignId}`,
    }),
    providesTags: (_result, _error, { campaignId }) => [
      { type: "AdminV2GrowthCampaigns", id: campaignId },
    ],
    keepUnusedDataFor: 20,
  }),

  getAdminV2GrowthCampaignPreview: builder.query<
    AdminV2GrowthCampaignPreviewResponseWithDelivery,
    AdminV2CampaignPreviewQuery
  >({
    query: ({
      campaignId,
      sampleLimit,
      localDate,
      localTime,
      includeRecipients,
      q,
      status,
      limit,
      offset,
    }) => ({
      url: `/admin/v2/growth/campaigns/${campaignId}/preview`,
      params: optionalQueryParams({
        ...(typeof sampleLimit === "number"
          ? { sampleLimit: Math.max(1, Math.floor(sampleLimit)) }
          : {}),
        ...(localDate !== undefined ? { localDate } : {}),
        ...(localTime !== undefined ? { localTime } : {}),
        ...(includeRecipients !== undefined ? { includeRecipients } : {}),
        ...(q !== undefined ? { q } : {}),
        ...(status !== undefined ? { status } : {}),
        ...(limit !== undefined ? { limit } : {}),
        ...(offset !== undefined ? { offset } : {}),
      }),
    }),
    providesTags: (_result, _error, { campaignId }) => [
      { type: "AdminV2GrowthCampaigns", id: campaignId },
    ],
    keepUnusedDataFor: 0,
  }),

  updateAdminV2GrowthCampaign: builder.mutation<
    AdminV2BroadcastCampaignRecordResponse,
    {
      campaignId: string;
      body: {
        name?: string;
        description?: string | null;
        conditionsSource?: AdminV2CampaignConditionsSource;
        segmentId?: string;
        sharedSegmentAudience?: AdminV2SharedSegmentSelection;
        crmActionScopeId?: string;
        crmActionScopeDigest?: string;
        crmActionAudienceMode?: "primary_user" | "all_users";
        templateId?: string;
        senderIdentityId?: string | null;
        channel?: AdminV2MessagingChannel;
        category?: AdminV2MessagingCategory;
        metadata?: AdminV2JsonValue;
        contentDraft?: AdminV2CampaignContentDraft;
        recipientExclusions?: AdminV2CampaignRecipientExclusion[];
      };
    }
  >({
    query: ({ campaignId, body }) => ({
      url: `/admin/v2/growth/campaigns/${campaignId}`,
      method: "PUT",
      data: body,
    }),
    invalidatesTags: (_result, _error, { campaignId }) => [
      { type: "AdminV2GrowthCampaigns", id: "LIST" },
      { type: "AdminV2GrowthCampaigns", id: campaignId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  scheduleAdminV2GrowthCampaign: builder.mutation<
    AdminV2GrowthCampaignSendResponse,
    {
      campaignId: string;
      body: (
        | { scheduledAt: string; schedule?: never }
        | {
            schedule: AdminV2RecipientLocalScheduleRequest;
            scheduledAt?: never;
          }
      ) & {
        approvalRequestId?: string;
        batchSize?: number;
        reviewedBroadcastSendProof?:
          AdminV2GrowthBroadcastReviewedSendProof | AdminV2CampaignReviewChecks;
        reviewId?: string;
      };
    }
  >({
    query: ({ campaignId, body }) => ({
      url: `/admin/v2/growth/campaigns/${campaignId}/schedule`,
      method: "POST",
      data: body,
    }),
    invalidatesTags: (_result, _error, { campaignId }) => [
      { type: "AdminV2GrowthCampaigns", id: "LIST" },
      { type: "AdminV2GrowthCampaigns", id: campaignId },
      { type: "AdminV2Approvals", id: "LIST" },
      { type: "AdminV2Jobs", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  sendNowAdminV2GrowthCampaign: builder.mutation<
    AdminV2GrowthCampaignSendResponse,
    {
      campaignId: string;
      body?: {
        approvalRequestId?: string;
        batchSize?: number;
        reviewedBroadcastSendProof?:
          AdminV2GrowthBroadcastReviewedSendProof | AdminV2CampaignReviewChecks;
        reviewId?: string;
      };
    }
  >({
    query: ({ campaignId, body }) => ({
      url: `/admin/v2/growth/campaigns/${campaignId}/send`,
      method: "POST",
      data: optionalMutationBody(body),
    }),
    invalidatesTags: (_result, _error, { campaignId }) => [
      { type: "AdminV2GrowthCampaigns", id: "LIST" },
      { type: "AdminV2GrowthCampaigns", id: campaignId },
      { type: "AdminV2Approvals", id: "LIST" },
      { type: "AdminV2Jobs", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  reviewAdminV2GrowthCampaignAudience: builder.mutation<
    { review: AdminV2CampaignAudienceReview },
    {
      campaignId: string;
      body: AdminV2CampaignReviewPreconditions & {
        checks: AdminV2CampaignReviewChecks;
        eligibleOnly?: CampaignEligibleOnlyReview;
      };
    }
  >({
    query: ({ campaignId, body }) => ({
      url: `/admin/v2/growth/campaigns/${campaignId}/audience-review`,
      method: "POST",
      data: body,
    }),
  }),

  attestAdminV2GrowthCampaignProductRelevance: builder.mutation<
    { attestation: { contentArtifactDigest: string; attestedAt: string } },
    { campaignId: string; expectedArtifactDigest: string }
  >({
    query: ({ campaignId, expectedArtifactDigest }) => ({
      url: `/admin/v2/growth/campaigns/${campaignId}/product-relevance-attestation`,
      method: "POST",
      data: { expectedArtifactDigest, confirmed: true },
    }),
  }),
  previewAdminV2GrowthCampaignContent: builder.mutation<
    AdminV2CampaignContentPreviewResponse,
    { campaignId: string; body: AdminV2CampaignContentPreviewRequest }
  >({
    query: ({ campaignId, body }) => ({
      url: `/admin/v2/growth/campaigns/${campaignId}/content-preview`,
      method: "POST",
      data: body,
    }),
  }),
  previewAdminV2GrowthCampaignContentTest: builder.mutation<
    AdminV2CampaignContentTestPreviewResponse,
    { campaignId: string; body: AdminV2CampaignContentTestPreviewRequest }
  >({
    query: ({ campaignId, body }) => ({
      url: `/admin/v2/growth/campaigns/${campaignId}/test-content/preview`,
      method: "POST",
      data: body,
    }),
  }),
  sendAdminV2GrowthCampaignContentTest: builder.mutation<
    AdminV2MessagingTemplateSelfTestSendResponse,
    { campaignId: string; body: AdminV2CampaignContentTestSendRequest }
  >({
    query: ({ campaignId, body }) => ({
      url: `/admin/v2/growth/campaigns/${campaignId}/test-content`,
      method: "POST",
      data: body,
    }),
  }),

  getAdminV2GrowthCampaignMetrics: builder.query<
    AdminV2GrowthCampaignMetricsResponse,
    { campaignId: string }
  >({
    query: ({ campaignId }) => ({
      url: `/admin/v2/growth/campaigns/${campaignId}/metrics`,
    }),
    providesTags: (_result, _error, { campaignId }) => [
      { type: "AdminV2GrowthCampaigns", id: campaignId },
    ],
    keepUnusedDataFor: 20,
  }),
});
